// src/pages/task/views/ListView.tsx
// Table/list view with density support and collapsible grouping.

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import {
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
} from '@react/components';

import type { Task } from '@core/types/task';
import type { DensityMode } from '@/features/workspace/types';

import { StatusSelect } from '@/components/StatusSelect';
import { PrioritySelect } from '@/components/PrioritySelect';

import {
  getEntityId,
  getTaskStatus,
  getTaskPriority,
  getTaskAssignee,
  getTaskDueDate,
  getTaskProgress,
  getTaskType,
  getTaskTypeInfo,
  getChecklistProgress,
  getDueDateUrgency,
  getStatusMapping,
  getPriorityColor,
  getPriorityIconPath,
  getInitials,
  getAvatarColor,
  getDisplayName,
  formatDate,
  taskStatusMap,
  taskPriorityMap,
} from '@/features/task/logic';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ListViewProps {
  tasks: Task[];
  density: DensityMode;
  groupBy: string | null;
  onTaskClick: (id: string) => void;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  onStatusChange?: (taskId: string, status: string) => void;
  onPriorityChange?: (taskId: string, priority: string) => void;
  onDueDateChange?: (taskId: string, date: string) => void;
  onTitleChange?: (taskId: string, title: string) => void;
}

// ---------------------------------------------------------------------------
// Density config
// ---------------------------------------------------------------------------

const DENSITY_ROW_HEIGHTS: Record<DensityMode, string> = {
  compact: 'h-8',
  normal: 'h-11',
  relaxed: 'h-16',
};

const DENSITY_TEXT: Record<DensityMode, string> = {
  compact: 'text-xs',
  normal: 'text-sm',
  relaxed: 'text-sm',
};

// ---------------------------------------------------------------------------
// Grouping helper
// ---------------------------------------------------------------------------

function groupTasks(
  tasks: Task[],
  groupBy: string | null,
): { key: string; label: string; tasks: Task[] }[] {
  if (!groupBy) {
    return [{ key: '__all', label: '', tasks }];
  }

  const groups: Record<string, Task[]> = {};
  const order: string[] = [];

  for (const task of tasks) {
    let value: string;
    switch (groupBy) {
      case 'status':
        value = getTaskStatus(task) || 'UNKNOWN';
        break;
      case 'priority':
        value = getTaskPriority(task) || 'UNKNOWN';
        break;
      case 'assignee': {
        const assignee = getTaskAssignee(task);
        value = assignee ? getDisplayName(assignee) : 'Unassigned';
        break;
      }
      case 'type':
        value = getTaskType(task) || 'UNKNOWN';
        break;
      default:
        value = 'Other';
    }

    if (!groups[value]) {
      groups[value] = [];
      order.push(value);
    }
    groups[value].push(task);
  }

  return order.map(key => ({
    key,
    label: key,
    tasks: groups[key],
  }));
}

function formatGroupLabel(groupBy: string, key: string, t: (k: string) => string): string {
  if (groupBy === 'status') {
    const mapping = taskStatusMap[key];
    return mapping ? t(mapping.labelKey) : key;
  }
  if (groupBy === 'priority') {
    const mapping = taskPriorityMap[key];
    return mapping ? t(mapping.labelKey) : key;
  }
  return key;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ListView = observer(({
  tasks,
  density,
  groupBy,
  onTaskClick,
  selectedIds: externalSelectedIds,
  onSelectionChange,
  onStatusChange,
  onPriorityChange,
  onDueDateChange,
  onTitleChange,
}: ListViewProps) => {
  const { t, locale } = useI18n();
  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null);

  // Use external selection state when provided, otherwise fall back to internal
  const selectedIds = externalSelectedIds ?? internalSelectedIds;
  const setSelectedIds = onSelectionChange ?? setInternalSelectedIds;

  const groups = useMemo(
    () => groupTasks(tasks, groupBy),
    [tasks, groupBy],
  );

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const toggleSelect = useCallback((id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  }, [selectedIds, setSelectedIds]);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === tasks.length) {
      setSelectedIds(new Set<string>());
    } else {
      setSelectedIds(new Set(tasks.map(t => getEntityId(t))));
    }
  }, [selectedIds, tasks, setSelectedIds]);

  const rowHeight = DENSITY_ROW_HEIGHTS[density];
  const textSize = DENSITY_TEXT[density];

  if (tasks.length === 0) {
    return (
      <Table>
        <TableBody>
          <TableEmpty colSpan={7}>{t('task.noItems')}</TableEmpty>
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <input
                type="checkbox"
                checked={selectedIds.size === tasks.length && tasks.length > 0}
                onChange={toggleSelectAll}
                className="rounded border-border"
                aria-label={t('common.selectAll')}
              />
            </TableHead>
            <TableHead className="w-8" />
            {density !== 'compact' && <TableHead className="w-20">Key</TableHead>}
            <TableHead>{t('task.field.title')}</TableHead>
            <TableHead className="w-24">{t('task.field.status')}</TableHead>
            <TableHead className="w-10">{t('task.field.priority')}</TableHead>
            {density !== 'compact' && <TableHead className="w-24">{t('task.field.dueDate')}</TableHead>}
            <TableHead className="w-10">{t('task.field.assignee')}</TableHead>
            {density === 'relaxed' && <TableHead className="w-24">{t('task.progress')}</TableHead>}
            {density === 'relaxed' && <TableHead className="w-16">{t('task.checklists')}</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map(group => (
            <GroupSection
              key={group.key}
              group={group}
              groupBy={groupBy}
              density={density}
              rowHeight={rowHeight}
              textSize={textSize}
              selectedIds={selectedIds}
              collapsed={collapsedGroups.has(group.key)}
              onToggleGroup={toggleGroup}
              onToggleSelect={toggleSelect}
              onTaskClick={onTaskClick}
              locale={locale}
              t={t}
              editingCell={editingCell}
              setEditingCell={setEditingCell}
              onStatusChange={onStatusChange}
              onPriorityChange={onPriorityChange}
              onDueDateChange={onDueDateChange}
              onTitleChange={onTitleChange}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Group section
// ---------------------------------------------------------------------------

interface GroupSectionProps {
  group: { key: string; label: string; tasks: Task[] };
  groupBy: string | null;
  density: DensityMode;
  rowHeight: string;
  textSize: string;
  selectedIds: Set<string>;
  collapsed: boolean;
  onToggleGroup: (key: string) => void;
  onToggleSelect: (id: string) => void;
  onTaskClick: (id: string) => void;
  locale: string;
  t: (key: string) => string;
  editingCell: { id: string; field: string } | null;
  setEditingCell: (cell: { id: string; field: string } | null) => void;
  onStatusChange?: (taskId: string, status: string) => void;
  onPriorityChange?: (taskId: string, priority: string) => void;
  onDueDateChange?: (taskId: string, date: string) => void;
  onTitleChange?: (taskId: string, title: string) => void;
}

function GroupSection({
  group,
  groupBy,
  density,
  rowHeight,
  textSize,
  selectedIds,
  collapsed,
  onToggleGroup,
  onToggleSelect,
  onTaskClick,
  locale,
  t,
  editingCell,
  setEditingCell,
  onStatusChange,
  onPriorityChange,
  onDueDateChange,
  onTitleChange,
}: GroupSectionProps) {
  const colSpan = density === 'relaxed' ? 10 : density === 'compact' ? 6 : 8;

  return (
    <>
      {/* Group header */}
      {groupBy && group.key !== '__all' && (
        <TableRow>
          <TableCell colSpan={colSpan}>
            <button
              onClick={() => onToggleGroup(group.key)}
              className="flex items-center gap-2 w-full text-left py-1"
              aria-expanded={!collapsed}
            >
              <svg
                className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${collapsed ? '' : 'rotate-90'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-sm font-semibold text-foreground">
                {formatGroupLabel(groupBy, group.label, t)}
              </span>
              <span className="text-xs text-muted-foreground">
                ({group.tasks.length})
              </span>
            </button>
          </TableCell>
        </TableRow>
      )}

      {/* Task rows */}
      {!collapsed && group.tasks.map(task => (
        <TaskRow
          key={getEntityId(task)}
          task={task}
          density={density}
          rowHeight={rowHeight}
          textSize={textSize}
          selected={selectedIds.has(getEntityId(task))}
          onToggleSelect={onToggleSelect}
          onTaskClick={onTaskClick}
          locale={locale}
          t={t}
          editingCell={editingCell}
          setEditingCell={setEditingCell}
          onStatusChange={onStatusChange}
          onPriorityChange={onPriorityChange}
          onDueDateChange={onDueDateChange}
          onTitleChange={onTitleChange}
        />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Task row
// ---------------------------------------------------------------------------

interface TaskRowProps {
  task: Task;
  density: DensityMode;
  rowHeight: string;
  textSize: string;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onTaskClick: (id: string) => void;
  locale: string;
  t: (key: string) => string;
  editingCell: { id: string; field: string } | null;
  setEditingCell: (cell: { id: string; field: string } | null) => void;
  onStatusChange?: (taskId: string, status: string) => void;
  onPriorityChange?: (taskId: string, priority: string) => void;
  onDueDateChange?: (taskId: string, date: string) => void;
  onTitleChange?: (taskId: string, title: string) => void;
}

function TaskRow({
  task,
  density,
  rowHeight,
  textSize,
  selected,
  onToggleSelect,
  onTaskClick,
  locale,
  t,
  editingCell,
  setEditingCell,
  onStatusChange,
  onPriorityChange,
  onDueDateChange,
  onTitleChange,
}: TaskRowProps) {
  const id = getEntityId(task);
  const status = getTaskStatus(task);
  const priority = getTaskPriority(task);
  const assignee = getTaskAssignee(task);
  const dueDate = getTaskDueDate(task);
  const progress = getTaskProgress(task);
  const taskType = getTaskType(task);
  const checklist = getChecklistProgress(task);

  const statusInfo = getStatusMapping(taskStatusMap, status);
  const typeInfo = getTaskTypeInfo(taskType);
  const priorityColor = getPriorityColor(priority);
  const priorityIconPath = getPriorityIconPath(priority);
  const dueUrgency = getDueDateUrgency(dueDate, status);
  const assigneeName = assignee ? getDisplayName(assignee) : null;

  return (
    <TableRow
      className={`${rowHeight} cursor-pointer transition-colors hover:bg-muted/50 ${selected ? 'bg-primary/5' : ''}`}
      onClick={() => onTaskClick(id)}
    >
      {/* Checkbox */}
      <TableCell onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(id)}
          className="rounded border-border"
          aria-label={`Select task ${(task as any).title || ''}`}
        />
      </TableCell>

      {/* Type icon */}
      <TableCell>
        {typeInfo && (
          <svg
            className="h-4 w-4 text-muted-foreground"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
            aria-label={t(typeInfo.labelKey)}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d={typeInfo.iconPath} />
          </svg>
        )}
      </TableCell>

      {/* Key (normal, relaxed only) */}
      {density !== 'compact' && (
        <TableCell>
          <span className={`${textSize} text-muted-foreground font-mono`}>
            {id.slice(0, 8)}
          </span>
        </TableCell>
      )}

      {/* Title + description snippet */}
      <TableCell onClick={e => e.stopPropagation()}>
        <InlineTitle
          id={id}
          title={(task as any).title || ''}
          description={density !== 'compact' ? (task as any).description : undefined}
          textSize={textSize}
          isEditing={editingCell?.id === id && editingCell?.field === 'title'}
          onStartEdit={onTitleChange ? () => setEditingCell({ id, field: 'title' }) : undefined}
          onSave={onTitleChange ? (newTitle) => { onTitleChange(id, newTitle); setEditingCell(null); } : undefined}
          onCancel={() => setEditingCell(null)}
          onTaskClick={onTaskClick}
          t={t}
        />
      </TableCell>

      {/* Status badge */}
      <TableCell onClick={e => e.stopPropagation()}>
        {onStatusChange ? (
          <StatusSelect
            value={status || ''}
            onSelect={(newStatus) => onStatusChange(id, newStatus)}
          />
        ) : (
          <Badge variant={statusInfo.variant as any} size="sm">
            {t(statusInfo.labelKey)}
          </Badge>
        )}
      </TableCell>

      {/* Priority */}
      <TableCell onClick={e => e.stopPropagation()}>
        {onPriorityChange ? (
          <PrioritySelect
            value={priority || ''}
            onSelect={(newPriority) => onPriorityChange(id, newPriority)}
          />
        ) : (
          priorityIconPath ? (
            <svg
              className={`h-4 w-4 ${priorityColor}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-label={priority ? t(getStatusMapping(taskPriorityMap, priority).labelKey) : undefined}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d={priorityIconPath} />
            </svg>
          ) : (
            <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30 inline-block" />
          )
        )}
      </TableCell>

      {/* Due date (normal, relaxed only) */}
      {density !== 'compact' && (
        <TableCell onClick={e => e.stopPropagation()}>
          {editingCell?.id === id && editingCell?.field === 'dueDate' && onDueDateChange ? (
            <InlineDateEditor
              value={dueDate || ''}
              onSave={(newDate) => { onDueDateChange(id, newDate); setEditingCell(null); }}
              onCancel={() => setEditingCell(null)}
            />
          ) : (
            (() => {
              const dueContent = dueDate ? (
                <>
                  {dueUrgency === 'overdue' && (
                    <svg className="h-3 w-3 inline mr-0.5 -mt-px" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                  {formatDate(dueDate, locale)}
                </>
              ) : (
                '--'
              );

              const urgencyClass =
                dueUrgency === 'overdue' ? 'text-destructive font-medium' :
                dueUrgency === 'soon' ? 'text-yellow-600 dark:text-yellow-400' :
                'text-muted-foreground';

              return onDueDateChange ? (
                <button
                  type="button"
                  className={`text-xs whitespace-nowrap cursor-pointer hover:bg-accent rounded px-1 py-0.5 -mx-1 ${urgencyClass}`}
                  onClick={() => setEditingCell({ id, field: 'dueDate' })}
                  aria-label={t('task.field.dueDate')}
                >
                  {dueContent}
                </button>
              ) : (
                <span className={`text-xs whitespace-nowrap ${urgencyClass}`}>{dueContent}</span>
              );
            })()
          )}
        </TableCell>
      )}

      {/* Assignee avatar */}
      <TableCell>
        {assigneeName ? (
          <div
            className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white flex-shrink-0"
            style={{ backgroundColor: getAvatarColor(assigneeName) }}
            title={assigneeName}
          >
            {getInitials(assigneeName)}
          </div>
        ) : (
          <div className="h-6 w-6 rounded-full border-2 border-dashed border-border flex items-center justify-center flex-shrink-0" title={t('task.unassigned')}>
            <svg className="h-3 w-3 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        )}
      </TableCell>

      {/* Progress bar (relaxed only) */}
      {density === 'relaxed' && (
        <TableCell>
          {progress > 0 ? (
            <div className="flex items-center gap-2">
              <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-[10px] text-muted-foreground w-7 text-right">{progress}%</span>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">--</span>
          )}
        </TableCell>
      )}

      {/* Checklist (relaxed only) */}
      {density === 'relaxed' && (
        <TableCell>
          {checklist ? (
            <span className="text-xs text-muted-foreground">
              {checklist.done}/{checklist.total}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">--</span>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Inline title editor
// ---------------------------------------------------------------------------

interface InlineTitleProps {
  id: string;
  title: string;
  description?: string;
  textSize: string;
  isEditing: boolean;
  onStartEdit?: () => void;
  onSave?: (newTitle: string) => void;
  onCancel: () => void;
  onTaskClick: (id: string) => void;
  t: (key: string) => string;
}

function InlineTitle({
  id,
  title,
  description,
  textSize,
  isEditing,
  onStartEdit,
  onSave,
  onCancel,
  onTaskClick,
  t,
}: InlineTitleProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(title);

  useEffect(() => {
    if (isEditing) {
      setDraft(title);
      // Focus on next tick after render
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isEditing, title]);

  if (isEditing && onSave) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const trimmed = draft.trim();
            if (trimmed && trimmed !== title) {
              onSave(trimmed);
            } else {
              onCancel();
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        onBlur={() => {
          const trimmed = draft.trim();
          if (trimmed && trimmed !== title) {
            onSave(trimmed);
          } else {
            onCancel();
          }
        }}
        className={`${textSize} font-medium text-foreground bg-background border border-border rounded px-1 py-0.5 w-full outline-none focus:ring-2 focus:ring-ring`}
      />
    );
  }

  return (
    <button
      type="button"
      className="min-w-0 w-full text-left"
      onDoubleClick={onStartEdit}
      onClick={() => onTaskClick(id)}
      title={onStartEdit ? t('task.doubleClickToEdit') : undefined}
    >
      <span className={`${textSize} font-medium text-foreground truncate block ${onStartEdit ? 'cursor-text' : ''}`}>
        {title || t('common.untitled')}
      </span>
      {description && (
        <span className="text-xs text-muted-foreground truncate block mt-0.5">
          {description.slice(0, 80)}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Inline date editor
// ---------------------------------------------------------------------------

interface InlineDateEditorProps {
  value: string;
  onSave: (newDate: string) => void;
  onCancel: () => void;
}

function InlineDateEditor({ value, onSave, onCancel }: InlineDateEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  // Click outside detection
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onCancel();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onCancel]);

  // Convert stored date to input format (YYYY-MM-DD)
  const inputValue = value ? value.slice(0, 10) : '';

  return (
    <div ref={containerRef}>
      <input
        ref={inputRef}
        type="date"
        defaultValue={inputValue}
        onChange={(e) => {
          if (e.target.value) {
            onSave(e.target.value);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const val = (e.target as HTMLInputElement).value;
            if (val) {
              onSave(val);
            } else {
              onCancel();
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        onBlur={() => {
          // Small delay to allow click-outside handler to fire first
          setTimeout(() => onCancel(), 150);
        }}
        className="text-xs text-foreground bg-background border border-border rounded px-1 py-0.5 outline-none focus:ring-2 focus:ring-ring"
      />
    </div>
  );
}
